import type { CSSProperties } from 'react';
import styles from './TakeChip.module.css';

interface Props {
  takeNumber: number;      // display number (e.g. `Take 3`) — uses block.takeCounter progression
  isFav: boolean;
  isNewest: boolean;
  isPlaying: boolean;
  onPlayToggle(): void;    // click on chip body toggles play/pause
  onFavToggle(): void;     // click on star toggles favorite
  onDelete(): void;        // click on X deletes the take (caller is responsible for confirming)
  /** Multi-Voice (#173): per-take voice shade (a CSS `var(--voice-N)`
   *  reference). When set, scopes `--brandcolor` to the voice color for
   *  this chip's subtree — favorite-state styling auto-uses the voice
   *  color instead of the project brand. Omit for single-voice assets. */
  voiceColor?: string;
  /** Once-per-device "click the star" hint anchored above this chip's star
   *  (lib/starHint.ts). The pill body stars the take; its × only dismisses. */
  hint?: boolean;
  onHintDismiss?(): void;
}

/** A single take-chip in the block footer. Matches legacy visual + behavior:
 *  star toggles favorite, label shows "Take N" (+ optional NEW badge for the
 *  most recent take), click-on-body plays/pauses, small X deletes. */
export function TakeChip({
  takeNumber,
  isFav,
  isNewest,
  isPlaying,
  onPlayToggle,
  onFavToggle,
  onDelete,
  voiceColor,
  hint = false,
  onHintDismiss,
}: Props) {
  const cls = [
    styles.chip,
    isFav && styles.fav,
    isPlaying && styles.playing,
  ]
    .filter(Boolean)
    .join(' ');

  const style: CSSProperties | undefined = voiceColor
    ? ({ ['--brandcolor' as string]: voiceColor } as CSSProperties)
    : undefined;

  return (
    <span className={cls} style={style} onClick={onPlayToggle}>
      {hint && (
        <span className={styles.hint} role="note">
          <button
            type="button"
            className={styles.hintBody}
            onClick={(e) => {
              e.stopPropagation();
              onFavToggle();
            }}
          >
            <i className="ph-fill ph-star" aria-hidden="true" />
            Click the star to place this take on the timeline, or click the take to listen again
          </button>
          <button
            type="button"
            className={styles.hintClose}
            onClick={(e) => {
              e.stopPropagation();
              onHintDismiss?.();
            }}
            aria-label="Dismiss hint"
          >
            <i className="ph-bold ph-x" aria-hidden="true" />
          </button>
        </span>
      )}
      <button
        type="button"
        className={styles.star}
        onClick={(e) => {
          e.stopPropagation();
          onFavToggle();
        }}
        aria-label={isFav ? 'Unfavorite take' : 'Favorite take'}
        aria-pressed={isFav}
      >
        <i className={isFav ? 'ph-fill ph-star' : 'ph-bold ph-star'} aria-hidden="true" />
      </button>
      <span className={styles.label}>
        Take {takeNumber}
        {isNewest && <span className={styles.new}> NEW</span>}
      </span>
      <button
        type="button"
        className={styles.del}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete take"
      >
        <i className="ph-bold ph-x" />
      </button>
    </span>
  );
}
